import React, { useRef, useEffect, useState } from 'react';
import '../../styles/ScrollableCardSlider.css';
import { ArrowLeft, ArrowRight } from "lucide-react";

type CardItem = {
  subheading: string;
  text: string;
  points: string[];
  image: string;
};

type ScrollableCardSliderProps = {
  sectionHeading: string;
  items: CardItem[];
};

// @ts-ignore
const imagesContext = require.context('../../assets/serviceDetails/tools', false, /\.(png|jpe?g|svg)$/);

const getImage = (imageName: string): string => {
  try {
    return imagesContext(`./${imageName}`);
  } catch {
    console.warn(`Image not found: ${imageName}`);
    return '';
  }
};

const ScrollableCardSlider: React.FC<ScrollableCardSliderProps> = ({ sectionHeading, items }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const scroll = (direction: 'left' | 'right') => {
    const container = sliderRef.current;
    if (!container) return;
    const scrollAmount = container.offsetWidth * 0.8;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;

      setIsAtStart(scrollLeft <= 0);
      setIsAtEnd(Math.ceil(scrollLeft) >= Math.floor(maxScrollLeft));
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="slider-container">
      <div className='slider-container-content'>
        <div className='slider-content-heading'>
          <p className="sections-title slider-heading">{sectionHeading}</p>
          <div className='slider-arrow-button'>
            <div
              className={`slider-arrow left ${isAtStart ? 'inactive' : ''}`}
              onClick={() => scroll('left')}
            >
              <ArrowLeft size={20} />
            </div>
            <div
              className={`slider-arrow right ${isAtEnd ? 'inactive' : ''}`}
              onClick={() => scroll('right')}
            >
              <ArrowRight size={20} />
            </div>
          </div>
        </div>

        <div className="slider-scroll-wrapper">
          <div className="slider-scroll" ref={sliderRef}>
            {items.map((item, index) => (
              <div className="slider-card" key={index}>
                <div className="slider-left">
                  <h3 className="slider-subheading">{item.subheading}</h3>
                  <p className="slider-text">{item.text}</p>
                  <ul className="slider-points">
                    {item.points.map((point, i) => (
                      <li key={i}>
                        <span className="checkmark">✔</span> {point}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="slider-right">
                  <img src={getImage(item.image)} alt={item.subheading} className="slider-image" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrollableCardSlider;
